import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react'
import { Link, useParams } from 'react-router-dom'
import styled from 'styled-components'
import { Container } from '../components/Animations'
import Breadcrumbs from '../components/Breadcrumbs'
import { PrimaryButton } from '../components/Button'
import { TextArea } from '../components/Input'
import Spinner from '../components/Spinner'
import TimeText from '../components/TimeText'
import { ListPage } from '../components/DashboardPrimitives'
import Radom, { ISupportTicketDetail } from '../state/Radom'
import { RADOM_COLORS } from '../util/Constants'
import { errorToast, successToast } from '../util/Util'
import { supportStatusBadge } from './Support'

const TicketHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const TicketSubject = styled.span`
  font-size: 20px;
  font-weight: 500;
  color: ${RADOM_COLORS.BLACK};
`

const TicketMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  font-size: 13px;
  color: ${RADOM_COLORS.GRAY_DARKER};
`

const Thread = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  max-width: 760px;
`

const MessageBubble = styled.div<{ $own?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 14px 16px;
  border-radius: 10px;
  max-width: 85%;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  font-size: 14px;
  line-height: 1.5;
  align-self: ${({ $own }) => $own ? 'flex-end' : 'flex-start'};
  background-color: ${({ $own }) => $own ? RADOM_COLORS.BLACK : 'white'};
  color: ${({ $own }) => $own ? 'white' : RADOM_COLORS.BLACK};
  border: 1px solid ${({ $own }) => $own ? RADOM_COLORS.BLACK : RADOM_COLORS.GRAY_MED};
`

const MessageMeta = styled.div<{ $own?: boolean }>`
  display: flex;
  gap: 8px;
  align-items: center;
  font-size: 12px;
  color: ${({ $own }) => $own ? RADOM_COLORS.GRAY_MED : RADOM_COLORS.GRAY_DARKER};
`

const ReplyBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-width: 760px;
`

const authorLabel = (author: string): string => {
  if (author === 'you') return 'You'
  if (author === 'assistant') return 'Radom Assistant'
  return 'Radom Support'
}

const SupportTicket = observer(() => {
  const params = useParams()
  const ticketId = params.id ?? ''

  const [detail, setDetail] = useState<ISupportTicketDetail | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [reply, setReply] = useState('')
  const [isSending, setIsSending] = useState(false)

  const loadTicket = (): void => {
    Radom.getSupportTicket(ticketId)
      .then(setDetail)
      .catch(err => errorToast(err.response?.data?.error || err.message))
      .finally(() => setIsLoading(false))
  }

  useEffect(() => {
    setIsLoading(true)
    loadTicket()
  }, [ticketId])

  const onSendReply = async (): Promise<void> => {
    if (!reply.trim()) return
    setIsSending(true)
    try {
      await Radom.createSupportMessage(ticketId, reply.trim())
      setReply('')
      successToast('Reply sent')
      loadTicket()
    } catch (err: any) {
      errorToast(err.response?.data?.error || err.message)
    } finally {
      setIsSending(false)
    }
  }

  if (isLoading) {
    return <Container>
      <ListPage>
        <Spinner />
      </ListPage>
    </Container>
  }

  if (!detail) {
    return <Container>
      <ListPage>
        <span>Ticket not found. <Link to='/support'>Back to support</Link></span>
      </ListPage>
    </Container>
  }

  const { ticket, messages } = detail

  return <Container>
    <ListPage>
      <Breadcrumbs items={[
        { item: 'Support', to: '/support' },
        { item: ticket.subject, to: '' }
      ]} />

      <TicketHeader>
        <TicketSubject>{ticket.subject}</TicketSubject>
        <TicketMeta>
          {supportStatusBadge(ticket.status)}
          <span style={{ textTransform: 'capitalize' }}>Opened via {ticket.source}</span>
          <TimeText date={new Date(ticket.createdAt)} />
        </TicketMeta>
      </TicketHeader>

      <Thread>
        {messages.map(message => {
          const own = message.author === 'you'
          return <MessageBubble key={message.id} $own={own}>
            <MessageMeta $own={own}>
              <span style={{ fontWeight: 500 }}>{authorLabel(message.author)}</span>
              <TimeText
                date={new Date(message.createdAt)}
                textColor={own ? RADOM_COLORS.GRAY_MED : undefined}
              />
            </MessageMeta>
            <span>{message.body}</span>
          </MessageBubble>
        })}
        {messages.length === 0 && <span style={{ color: RADOM_COLORS.GRAY_DARKER }}>No messages yet</span>}
      </Thread>

      <ReplyBox>
        <TextArea
          rows={4}
          value={reply}
          placeholder={ticket.status === 'resolved'
            ? 'Reply to reopen this ticket'
            : 'Write a reply'}
          onChange={e => setReply(e.target.value)}
        />
        <div>
          <PrimaryButton isLoading={isSending} disabled={!reply.trim()} onClick={() => { onSendReply() }}>
            Send reply
          </PrimaryButton>
        </div>
      </ReplyBox>
    </ListPage>
  </Container>
})

export default SupportTicket
